// Third-party
import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';

// Components
import { PrimaryButton, CancelButton } from '../buttons';
import QuestionnaireView from './QuestionnaireView';

// Utils
import { formatMessage } from '../../../utils/i18n';
import { userHasPermissionEnabled, PERMISSIONS } from '../../../utils/permissions';

// Actions
import { getContactName } from '../../../utils/contacts';
import QuestionnaireSectionItem from '@/types/questionnaire-section-item';
import QuestionItem from '@/types/question-item';
import QuestionOptionItem from '@/types/question-option-item';
import SavedSections from '@/types/saved-sections';
import Questionnaire from '@/types/questionnaire';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import GenericContact from '@/types/generic-contact';

type DispatchProps = {
  sectionIsDisabled: (section: QuestionnaireSectionItem) => boolean;
  setQuestionnaireComplete: () => void;
  getUnansweredQuestions: (section: QuestionnaireSectionItem) => QuestionItem[];
  getSectionSubtotal: (section: QuestionnaireSectionItem) => number;
  goBackQuestionnaire: () => void;
  saveAnswers: (sectionId: string, questionId: string, option: QuestionOptionItem, text?: string) => void;
  clearAnswers?: (sectionId: string, questionId: string) => void;
  saveNotes?: (sectionId: string, questionId: string, notes?: string) => void;
  clearNotes?: (sectionId: string, questionId: string) => void;
};

type ComponentsProps = {
  customQuestionnaireView?: JSX.Element;
  customQuestionnaireViewHeader?: JSX.Element;
};

type Props = DispatchProps &
  ComponentsProps & {
    savedSections: SavedSections;
    isCompleted: boolean;
    questionnaire: Questionnaire;
    user?: Nullable<User>;
    current?: {
      contact: GenericContact;
    };
    onClose?: () => void;
    onSave?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    title?: string;
    saveLabel?: string;
    userPermissionType?: string;
    disableSave?: boolean;
    displayEditDelete?: boolean;
    isSaving?: boolean;
  };

class QuestionnaireViewContainer extends React.Component<Props> {
  private scrollContainer?: HTMLElement;

  static defaultProps = {
    onClose: () => {},
    readOnly: false,
  };

  scrollQuestionnaire = (questionItem?: QuestionItem) => {
    if (!this.scrollContainer) {
      return;
    }

    if (questionItem) {
      const element = document.getElementById(`question-${questionItem._id}`);
      if (!element) {
        return;
      }

      return (this.scrollContainer.scrollTop = element.offsetTop);
    }

    this.scrollContainer.scrollTop = 0;
  };

  scrollToElement = (id?: string) => {
    if (!this.scrollContainer) {
      return;
    }

    if (id) {
      const element = document.getElementById(id);
      if (!element) {
        return;
      }

      return (this.scrollContainer.scrollTop = element.offsetTop - 50);
    }
  };

  onClose = () => {
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  hasUnansweredInSections = () => {
    const { savedSections, questionnaire } = this.props;

    return Object.keys(savedSections).some(sectionId => {
      const section = questionnaire.sections.find(s => s._id === sectionId);
      return section !== undefined && this.props.getUnansweredQuestions(section).length > 0;
    });
  };

  render() {
    const {
      current,
      isCompleted,
      userPermissionType,
      questionnaire,
      onEdit,
      onDelete,
      user,
      title,
      saveLabel,
      onSave,
      disableSave,
      customQuestionnaireView,
      displayEditDelete,
      isSaving,
    } = this.props;

    if (!questionnaire) {
      return null;
    }

    const profileOnly = !!user && userHasPermissionEnabled(user, PERMISSIONS.RISK_PROFILE) && !userHasPermissionEnabled(user, PERMISSIONS.RISK_QUESTIONNAIRE);
    const canSave = !displayEditDelete && (isCompleted || profileOnly || questionnaire.sections.length === 0);

    return (
      <div className="questionnaire-view-container">
        <div className="questionnaire-view-header d-flex align-items-center">
          <h1 className="flex-grow-1" data-private data-dd-privacy="mask">
            {title !== undefined && <span>{title}</span>}

            {current && title === undefined && getContactName(current.contact)}
          </h1>

          {displayEditDelete && (
            <>
              <PrimaryButton className="delete-answers-button" onClick={onDelete}>
                <span>{formatMessage('Comparison.Questionnaire.Delete.ButtonTitle')}</span>
              </PrimaryButton>
              <PrimaryButton onClick={onEdit}>
                <span>{formatMessage('Comparison.Questionnaire.Edit')}</span>
              </PrimaryButton>
            </>
          )}

          <CancelButton disabled={isSaving} onClick={this.onClose} />

          {canSave && onSave && (
            <PrimaryButton loading={isSaving} onClick={onSave} disabled={disableSave || this.hasUnansweredInSections()}>
              <span>{saveLabel || formatMessage('Common.Save')}</span>
            </PrimaryButton>
          )}
        </div>

        {questionnaire && (
          <PerfectScrollbar
            containerRef={ref => {
              this.scrollContainer = ref;
            }}
          >
            {customQuestionnaireView ? (
              <div className="questionnaire-view">{React.cloneElement(customQuestionnaireView, { scrollTo: this.scrollToElement })}</div>
            ) : (
              <QuestionnaireView
                isCompleted={isCompleted}
                setQuestionnaireComplete={this.props.setQuestionnaireComplete}
                getUnansweredQuestions={this.props.getUnansweredQuestions}
                getSectionSubtotal={this.props.getSectionSubtotal}
                sectionIsDisabled={this.props.sectionIsDisabled}
                goBackQuestionnaire={this.props.goBackQuestionnaire}
                saveAnswers={this.props.saveAnswers}
                clearAnswers={this.props.clearAnswers}
                userPermissionType={userPermissionType}
                contact={current && current.contact}
                questionnaire={questionnaire}
                scrollQuestionnaire={this.scrollQuestionnaire}
                savedSections={this.props.savedSections}
                customHeader={this.props.customQuestionnaireViewHeader}
                saveNotes={this.props.saveNotes}
                clearNotes={this.props.clearNotes}
              />
            )}
          </PerfectScrollbar>
        )}
      </div>
    );
  }
}

export default QuestionnaireViewContainer;
